import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin,
  type DragStartEvent,
  type DragEndEvent
} from '@dnd-kit/core'
import {
  format,
  startOfMonth,
  startOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday
} from 'date-fns'
import { ja } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, X, Plus, Eye, Download, Image, File, FileText, Film, Music, Archive, ArrowDownUp, UserPlus, TrendingUp, Clock } from 'lucide-react'
import { useStore } from '../store/useStore'
import type { Todo, Project } from '../lib/supabase'
import { toTimeOnly } from '../utils/dateUtils'
import AddTaskModal from './AddTaskModal'
import RichTextEditor from './RichTextEditor'
import TodoImageModal from './TodoImageModal'
import TodoFileModal from './TodoFileModal'
import { getTodoImages, getThumbnailUrl, getOptimizedImageUrl, type TodoImage } from '../lib/imageStorage'
import { getTodoFiles, getFileUrl, downloadFile, type TodoFile } from '../lib/fileStorage'
import { getHoliday, detectUserCountry, type CountryCode } from '../utils/holidays'
import { useFeedbackSettings } from '../hooks/useFeedbackSettings'
import { getMilestonesByProjectIds, type ProjectMilestone } from '../services/milestoneService'
import { triggerFeedback, initializeFeedback } from '../lib/feedback'
import ConfirmDialog from './ConfirmDialog'
import { getUserColorFromId, getUserInitial } from '../utils/userColor'
import { AssignTaskModal } from './team/AssignTaskModal'
import { TaskProgressPanel } from './team/TaskProgressPanel'
import { useFontSize } from '../contexts/FontSizeContext'

// Button SVG images (same as TodoItem)
const ButtonF = '/img/buttons/ButtonF.svg' // ゴミ箱に移動
const ButtonL = '/img/buttons/ButtonL.svg' // タスクを複製

type FilterStatus = 'pending' | 'in_progress' | 'completed' | 'deleted'
type SortOrder = 'due_date' | 'start_date' | 'display_order'

// 帯（期間バー）の背景色に対して読みやすい文字色を返す
const getBarTextColor = (hexColor: string): string => {
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.65 ? '#1f2937' : '#ffffff'
}

// 週内に描画する期間タスクの帯1本分
interface WeekBarSegment {
  todo: Todo
  colStart: number // 1〜7（週内の開始列）
  colSpan: number
  lane: number // 0始まりのレーン番号
  isStart: boolean // タスクの実際の開始日がこの週にある（左端を丸める）
  isEnd: boolean // タスクの実際の終了日がこの週にある（右端を丸める）
}

// 日付セル: ドロップ先（帯ドラッグでの日付変更用）
function DroppableDayCell({ dateKey, className, style, title, onClick, children }: {
  dateKey: string
  className: string
  style: React.CSSProperties
  title?: string
  onClick: () => void
  children: React.ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dateKey })
  return (
    <button
      ref={setNodeRef}
      onClick={onClick}
      className={`${className} ${isOver ? 'ring-2 ring-inset ring-indigo-500 bg-indigo-100' : ''}`}
      style={style}
      title={title}
    >
      {children}
    </button>
  )
}

// 期間タスクの帯: ドラッグ元（長押し/ドラッグで日付を平行移動）
function DraggableTaskBar({ dragId, todoId, segStartKey, className, style, title, onClick, children }: {
  dragId: string
  todoId: string
  segStartKey: string
  className: string
  style: React.CSSProperties
  title?: string
  onClick: (e: React.MouseEvent) => void
  children: React.ReactNode
}) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: dragId,
    data: { todoId, segStartKey }
  })
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`${className} ${isDragging ? 'opacity-30' : ''}`}
      style={style}
      title={title}
    >
      {children}
    </button>
  )
}

// Helper function to get color with opacity (same as TodoItem)
const getColorWithOpacity = (hexColor: string, opacity: number): string => {
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

// File category icon helper (same as TodoFileModal)
const getCategoryIcon = (category: TodoFile['file_category']) => {
  switch (category) {
    case 'document':
      return <FileText className="h-4 w-4 text-blue-500" />
    case 'video':
      return <Film className="h-4 w-4 text-purple-500" />
    case 'audio':
      return <Music className="h-4 w-4 text-green-500" />
    case 'archive':
      return <Archive className="h-4 w-4 text-orange-500" />
    default:
      return <File className="h-4 w-4 text-gray-500" />
  }
}

// Parse date string as local date (not UTC) to avoid timezone issues
const parseLocalDate = (dateStr: string): Date => {
  const datePart = dateStr.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  return new Date(year, month - 1, day)
}

// Format file size helper (same as TodoFileModal)
const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '不明'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Check if file can be previewed
const canPreview = (file: TodoFile): boolean => {
  const fileType = file.file_type || ''
  if (fileType.startsWith('image/')) return true
  if (fileType === 'application/pdf') return true
  if (fileType === 'text/plain' || fileType === 'text/csv') return true
  const officeTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ]
  if (officeTypes.includes(fileType)) return true
  return false
}

interface CalendarViewProps {
  projectId?: string | null
  filterStatuses?: FilterStatus[]
}

interface ImageWithUrl extends TodoImage {
  thumbnailUrl?: string
  optimizedUrl?: string
}

export default function CalendarView({ projectId, filterStatuses }: CalendarViewProps) {
  const { todos, projects, updateTodo, addTodo, fetchTodos, isPersonalSectionVisible, isTeamSectionVisible } = useStore()
  const { soundEnabled } = useFeedbackSettings()
  const { fontSize } = useFontSize()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedTask, setSelectedTask] = useState<Todo | null>(null)
  const [showAddTaskModal, setShowAddTaskModal] = useState(false)
  const [addTaskDate, setAddTaskDate] = useState<Date | null>(null)
  const [taskImages, setTaskImages] = useState<ImageWithUrl[]>([])
  const [taskFiles, setTaskFiles] = useState<TodoFile[]>([])
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null)
  const [holidayCountry] = useState<CountryCode>(() => detectUserCountry())

  // モーダル用ステート
  const [showImageModal, setShowImageModal] = useState(false)
  const [showFileModal, setShowFileModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showProgressPanel, setShowProgressPanel] = useState(false)

  // 編集フォーム用ステート
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editStatus, setEditStatus] = useState<Todo['status']>('pending')
  const [editStartDate, setEditStartDate] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [editDueTime, setEditDueTime] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // 編集フォーム用refs
  const editStartDateRef = useRef<HTMLInputElement>(null)
  const editDueDateRef = useRef<HTMLInputElement>(null)
  const editDueTimeRef = useRef<HTMLInputElement>(null)

  const taskListRef = useRef<HTMLDivElement>(null)
  const taskRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // 並び替え用ステート
  const [sortOrder, setSortOrder] = useState<SortOrder>('due_date')

  // マイルストーン用ステート
  const [milestones, setMilestones] = useState<ProjectMilestone[]>([])

  // Filter tasks with due dates
  const tasksWithDueDate = useMemo(() => {
    let filtered = todos.filter(todo =>
      todo.due_date &&
      todo.status !== 'deleted'
    )

    // Filter by project if specified
    if (projectId) {
      filtered = filtered.filter(todo => todo.project_id === projectId)
    } else {
      // 「全プロジェクト」選択時：セクション表示状態に応じてフィルタリング
      filtered = filtered.filter(todo => {
        const project = projects.find(p => p.id === todo.project_id)
        if (!project) return true // プロジェクトが見つからない場合は表示
        const isTeamProject = project.is_team_project && project.team_id
        if (isTeamProject) {
          return isTeamSectionVisible
        } else {
          return isPersonalSectionVisible
        }
      })
    }

    // Apply status filter if specified
    if (filterStatuses && filterStatuses.length > 0) {
      filtered = filtered.filter(todo => filterStatuses.includes(todo.status as FilterStatus))
    }

    // Sort based on sortOrder
    return filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'due_date':
          return new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime()
        case 'start_date':
          // If start_date is null, use due_date as fallback
          const aStart = a.start_date ? new Date(a.start_date).getTime() : new Date(a.due_date!).getTime()
          const bStart = b.start_date ? new Date(b.start_date).getTime() : new Date(b.due_date!).getTime()
          return aStart - bStart
        case 'display_order':
          return a.display_order - b.display_order
        default:
          return new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime()
      }
    })
  }, [todos, projectId, filterStatuses, sortOrder, projects, isPersonalSectionVisible, isTeamSectionVisible])

  // Group tasks by date
  const tasksByDate = useMemo(() => {
    const grouped = new Map<string, Todo[]>()
    tasksWithDueDate.forEach(todo => {
      const dateKey = format(parseLocalDate(todo.due_date!), 'yyyy-MM-dd')
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, [])
      }
      grouped.get(dateKey)!.push(todo)
    })
    return grouped
  }, [tasksWithDueDate])

  // Get task count for a specific date
  const getTaskCount = useCallback((date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd')
    return tasksByDate.get(dateKey)?.length || 0
  }, [tasksByDate])

  // Get project by ID
  const getProject = useCallback((projectId: string): Project | undefined => {
    return projects.find(p => p.id === projectId)
  }, [projects])

  // マイルストーンを取得
  useEffect(() => {
    const fetchMilestones = async () => {
      // 表示対象のプロジェクトIDを取得
      let projectIds: string[] = []
      if (projectId) {
        projectIds = [projectId]
      } else {
        // 全プロジェクト（一時保存を除く、セクション表示に基づく）
        projectIds = projects
          .filter(p => p.name !== '一時保存')
          .filter(p => {
            const isTeamProject = p.is_team_project && p.team_id
            if (isTeamProject) {
              return isTeamSectionVisible
            } else {
              return isPersonalSectionVisible
            }
          })
          .map(p => p.id)
      }

      if (projectIds.length === 0) {
        setMilestones([])
        return
      }

      try {
        const dbMilestones = await getMilestonesByProjectIds(projectIds)
        setMilestones(dbMilestones)
      } catch (error) {
        console.error('Failed to fetch milestones:', error)
      }
    }

    fetchMilestones()
  }, [projectId, projects, isPersonalSectionVisible, isTeamSectionVisible])

  // 特定の日付のマイルストーンを取得
  const getMilestonesForDate = useCallback((date: Date): ProjectMilestone[] => {
    const dateKey = format(date, 'yyyy-MM-dd')
    return milestones.filter(m => m.start_date === dateKey)
  }, [milestones])

  // Generate calendar days - always 6 weeks (42 days) for consistent height
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth)
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 })

    // Always generate exactly 42 days (6 weeks) for consistent layout
    const days: Date[] = []
    for (let i = 0; i < 42; i++) {
      days.push(addDays(startDate, i))
    }
    return days
  }, [currentMonth])

  // 帯に表示するサムネイル画像URL（todoId → 署名付きURL）
  const [barThumbUrls, setBarThumbUrls] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    let cancelled = false
    const loadBarThumbs = async () => {
      const targets = tasksWithDueDate.filter(t => t.thumbnail_image?.image_url)
      if (targets.length === 0) {
        setBarThumbUrls(new Map())
        return
      }
      const entries = await Promise.all(
        targets.map(async (t) => {
          const url = await getThumbnailUrl(t.thumbnail_image!.image_url!)
          return [t.id, url] as const
        })
      )
      if (!cancelled) {
        setBarThumbUrls(new Map(entries.filter((e): e is [string, string] => !!e[1])))
      }
    }
    loadBarThumbs()
    return () => { cancelled = true }
  }, [tasksWithDueDate])

  // 週ごとの期間タスク帯レイアウトを計算（Googleカレンダーの終日予定方式）
  // レーン数に上限は設けない（週の高さが自動で伸びる）。上限で切り捨てると
  // ドラッグで飽和週に移動した帯が不可視になるため。
  const weekBars = useMemo(() => {
    const weeks: { segments: WeekBarSegment[]; laneCount: number }[] = []
    const DAY_MS = 86400000
    for (let w = 0; w < 6; w++) {
      const weekStart = calendarDays[w * 7]
      const weekEnd = calendarDays[w * 7 + 6]
      // この週と期間が重なるタスクを抽出（開始日なしは期限日のみの1日帯）
      const candidates = tasksWithDueDate
        .map(todo => {
          const due = parseLocalDate(todo.due_date!)
          const start = todo.start_date ? parseLocalDate(todo.start_date) : due
          return start <= due
            ? { todo, start, end: due }
            : { todo, start: due, end: start }
        })
        .filter(({ start, end }) => start <= weekEnd && end >= weekStart)
        // 開始が早い順 → 同着は期間が長い順（上のレーンに長い帯が来る）
        .sort((a, b) =>
          a.start.getTime() - b.start.getTime() ||
          (b.end.getTime() - b.start.getTime()) - (a.end.getTime() - a.start.getTime())
        )

      const laneEnds: number[] = [] // 各レーンの占有済み最終列
      const segments: WeekBarSegment[] = []
      for (const { todo, start, end } of candidates) {
        const segStart = start < weekStart ? weekStart : start
        const segEnd = end > weekEnd ? weekEnd : end
        const colStart = Math.round((segStart.getTime() - weekStart.getTime()) / DAY_MS) + 1
        const colEnd = Math.round((segEnd.getTime() - weekStart.getTime()) / DAY_MS) + 1
        // 空いているレーンを探す（なければ新しいレーンを追加）
        let lane = laneEnds.findIndex(endCol => endCol < colStart)
        if (lane === -1) {
          lane = laneEnds.length
          laneEnds.push(0)
        }
        laneEnds[lane] = colEnd
        segments.push({
          todo,
          colStart,
          colSpan: colEnd - colStart + 1,
          lane,
          isStart: start >= weekStart,
          isEnd: end <= weekEnd
        })
      }
      weeks.push({ segments, laneCount: laneEnds.length })
    }
    return weeks
  }, [calendarDays, tasksWithDueDate])

  // 帯ドラッグ（日付変更）用のセンサー
  // マウス: 5px動かしたらドラッグ開始（通常クリックと共存）
  // タッチ: 250ms長押しでドラッグ開始（スクロールと共存）
  const barDragSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  )

  // ドラッグ中の帯情報（DragOverlay表示用）
  const [activeBarDrag, setActiveBarDrag] = useState<{ title: string; color: string; textColor: string } | null>(null)

  const handleBarDragStart = (event: DragStartEvent) => {
    const todoId = event.active.data.current?.todoId as string | undefined
    const todo = tasksWithDueDate.find(t => t.id === todoId)
    if (todo) {
      const color = getProject(todo.project_id)?.color || '#6366f1'
      setActiveBarDrag({ title: todo.title, color, textColor: getBarTextColor(color) })
      triggerHapticFeedback()
    }
  }

  // ドロップで開始日・期限日を同じ日数だけ平行移動（時刻部分は維持）
  const handleBarDragEnd = async (event: DragEndEvent) => {
    setActiveBarDrag(null)
    const { active, over } = event
    if (!over) return
    const todoId = active.data.current?.todoId as string | undefined
    const segStartKey = active.data.current?.segStartKey as string | undefined
    const todo = tasksWithDueDate.find(t => t.id === todoId)
    if (!todo || !segStartKey) return

    const dropKey = String(over.id) // 'yyyy-MM-dd'
    const deltaDays = Math.round(
      (parseLocalDate(dropKey).getTime() - parseLocalDate(segStartKey).getTime()) / 86400000
    )
    if (deltaDays === 0) return

    const shiftDate = (value: string | null): string | null => {
      if (!value) return null
      const [datePart, timePart] = value.split('T')
      const shifted = format(addDays(parseLocalDate(datePart), deltaDays), 'yyyy-MM-dd')
      return timePart ? `${shifted}T${timePart}` : shifted
    }

    try {
      await updateTodo(todo.id, {
        start_date: shiftDate(todo.start_date),
        due_date: shiftDate(todo.due_date)
      })
      triggerHapticFeedback()
    } catch (error) {
      console.error('Failed to move task dates:', error)
    }
  }

  // 帯クリック：期限日を選択して下のタスク一覧の該当タスクへスクロール
  const handleBarClick = (todo: Todo, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!todo.due_date) return
    const d = parseLocalDate(todo.due_date)
    setSelectedDate(d)
    if (!isSameMonth(d, currentMonth)) {
      setCurrentMonth(d)
    }
    requestAnimationFrame(() => {
      const el = taskRefs.current.get(todo.id)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  // Handle date click on calendar - always open new task modal
  const handleDateClick = (date: Date) => {
    setSelectedDate(date)
    setAddTaskDate(date)
    setShowAddTaskModal(true)
  }

  // Handle task count click - show existing tasks (always scroll to task, never open modal)
  const handleTaskCountClick = (date: Date, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedDate(date)

    const dateKey = format(date, 'yyyy-MM-dd')
    const tasksOnDate = tasksByDate.get(dateKey)

    if (tasksOnDate && tasksOnDate.length > 0) {
      // Always scroll to first task (regardless of count)
      const taskElement = taskRefs.current.get(tasksOnDate[0].id)
      if (taskElement) {
        taskElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  // 現在表示中のタスクインデックスを追跡
  const currentTaskIndexRef = useRef(0)
  const lastSnapIndexRef = useRef(-1)

  // ハプティックフィードバック（モバイル用）
  const triggerHapticFeedback = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10) // 短い振動
    }
  }, [])

  // Handle task scroll - update selected date based on visible task
  const handleTaskScroll = useCallback(() => {
    if (!taskListRef.current) return

    const containerRect = taskListRef.current.getBoundingClientRect()
    const containerTop = containerRect.top

    // Find the task closest to the top of the visible area
    let closestTaskId: string | null = null
    let closestDistance = Infinity
    let closestIndex = 0

    taskRefs.current.forEach((element, taskId) => {
      const rect = element.getBoundingClientRect()
      const distance = Math.abs(rect.top - containerTop)

      if (distance < closestDistance) {
        closestDistance = distance
        closestTaskId = taskId
        // タスクのインデックスを取得
        const index = tasksWithDueDate.findIndex(t => t.id === taskId)
        if (index !== -1) closestIndex = index
      }
    })

    // タスクが変わった時にハプティックフィードバック（モバイル）
    if (closestIndex !== lastSnapIndexRef.current && closestDistance < 20) {
      lastSnapIndexRef.current = closestIndex
      // タッチデバイスの場合のみ振動
      if ('ontouchstart' in window) {
        triggerHapticFeedback()
      }
    }

    currentTaskIndexRef.current = closestIndex

    if (closestTaskId) {
      const closestTask = tasksWithDueDate.find(t => t.id === closestTaskId)
      if (closestTask && closestTask.due_date) {
        const taskDate = parseLocalDate(closestTask.due_date)
        if (!isSameDay(taskDate, selectedDate)) {
          setSelectedDate(taskDate)
          // Also update month if needed
          if (!isSameMonth(taskDate, currentMonth)) {
            setCurrentMonth(taskDate)
          }
        }
      }
    }
  }, [tasksWithDueDate, selectedDate, currentMonth, triggerHapticFeedback])

  // カスタムホイールスクロール - 緩やかなスナップ
  const wheelTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isAnimatingRef = useRef(false)

  const scrollToTask = useCallback((index: number, smooth = true) => {
    if (tasksWithDueDate.length === 0) return

    const clampedIndex = Math.max(0, Math.min(tasksWithDueDate.length - 1, index))
    const targetTask = tasksWithDueDate[clampedIndex]
    const targetElement = taskRefs.current.get(targetTask.id)

    if (targetElement && taskListRef.current) {
      isAnimatingRef.current = true
      const containerRect = taskListRef.current.getBoundingClientRect()
      const elementRect = targetElement.getBoundingClientRect()
      const scrollOffset = elementRect.top - containerRect.top + taskListRef.current.scrollTop

      taskListRef.current.scrollTo({
        top: scrollOffset - 8,
        behavior: smooth ? 'smooth' : 'auto'
      })

      currentTaskIndexRef.current = clampedIndex

      setTimeout(() => {
        isAnimatingRef.current = false
      }, smooth ? 200 : 50)
    }
  }, [tasksWithDueDate])

  const handleWheel = useCallback((_e: WheelEvent) => {
    if (!taskListRef.current || tasksWithDueDate.length === 0) return

    // 通常のスクロールを許可（preventDefaultを削除）
    // スナップは緩やかに行う

    // 前回のタイムアウトをクリア
    if (wheelTimeoutRef.current) {
      clearTimeout(wheelTimeoutRef.current)
    }

    // スクロールが止まったら（300ms経過）軽くスナップ
    wheelTimeoutRef.current = setTimeout(() => {
      if (!isAnimatingRef.current) {
        scrollToTask(currentTaskIndexRef.current, true)
      }
    }, 300)
  }, [tasksWithDueDate, scrollToTask])

  // タッチスクロール後のスナップ処理（モバイル用）
  const touchStartYRef = useRef(0)
  const isTouchScrollingRef = useRef(false)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY
    isTouchScrollingRef.current = true
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (!isTouchScrollingRef.current) return
    isTouchScrollingRef.current = false

    // スクロール終了後に緩くスナップ（300ms待ってからスナップ）
    setTimeout(() => {
      if (taskListRef.current) {
        // 現在最も近いタスクにスナップ
        scrollToTask(currentTaskIndexRef.current, true)
        triggerHapticFeedback()
      }
    }, 300)
  }, [scrollToTask, triggerHapticFeedback])

  // ホイールイベントリスナー
  useEffect(() => {
    const taskList = taskListRef.current
    if (!taskList) return

    taskList.addEventListener('wheel', handleWheel, { passive: true })
    return () => taskList.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  // Throttled scroll handler
  useEffect(() => {
    const taskList = taskListRef.current
    if (!taskList) return

    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleTaskScroll()
          ticking = false
        })
        ticking = true
      }
    }

    taskList.addEventListener('scroll', onScroll)
    return () => taskList.removeEventListener('scroll', onScroll)
  }, [handleTaskScroll])

  // Handle task click - open detail modal and load attachments
  const handleTaskClick = async (todo: Todo) => {
    setSelectedTask(todo)
    setTaskImages([])
    setTaskFiles([])
    setLoadingAttachments(true)

    // 編集フォームを初期化
    setEditTitle(todo.title)
    setEditDescription(todo.description || '')
    setEditStatus(todo.status)
    if (todo.start_date) {
      // Extract date part directly from string to avoid timezone issues
      setEditStartDate(todo.start_date.split('T')[0])
    } else {
      setEditStartDate('')
    }
    if (todo.due_date) {
      // Extract date and time parts directly from string to avoid timezone issues
      setEditDueDate(todo.due_date.split('T')[0])
      const timePart = todo.due_date.includes('T') ? todo.due_date.split('T')[1]?.substring(0, 5) : ''
      setEditDueTime(timePart && timePart !== '00:00' ? timePart : '')
    } else {
      setEditDueDate('')
      setEditDueTime('')
    }

    try {
      // Load images and files in parallel
      const [imagesResult, filesResult] = await Promise.all([
        getTodoImages(todo.id),
        getTodoFiles(todo.id)
      ])

      if (imagesResult.success && imagesResult.images) {
        const imagesWithUrls = await Promise.all(
          imagesResult.images.map(async (img) => ({
            ...img,
            thumbnailUrl: await getThumbnailUrl(img.image_url),
            optimizedUrl: await getOptimizedImageUrl(img.image_url)
          }))
        )
        setTaskImages(imagesWithUrls)
      }

      if (filesResult.success && filesResult.files) {
        setTaskFiles(filesResult.files)
      }
    } catch (error) {
      console.error('Failed to load attachments:', error)
    } finally {
      setLoadingAttachments(false)
    }
  }

  // Handle add task button click on a date
  const handleAddTaskClick = (date: Date, e: React.MouseEvent) => {
    e.stopPropagation()
    setAddTaskDate(date)
    setShowAddTaskModal(true)
  }

  // Handle status change from modal
  const handleStatusChange = async (todo: Todo, newStatus: Todo['status']) => {
    initializeFeedback()
    await updateTodo(todo.id, { status: newStatus })

    if (newStatus === 'completed') {
      triggerFeedback('complete', { soundEnabled })
    } else if (newStatus === 'deleted') {
      triggerFeedback('delete', { soundEnabled })
    }
    setSelectedTask(null)
  }

  // Handle save task changes
  const handleSaveTask = async () => {
    if (!selectedTask || !editTitle.trim()) return

    setIsSaving(true)
    try {
      initializeFeedback()

      // Build due_date with time if provided (use local time format, not UTC)
      let dueDate: string | null = null
      if (editDueDate) {
        if (editDueTime) {
          dueDate = `${editDueDate}T${editDueTime}:00`
        } else {
          dueDate = `${editDueDate}T00:00:00`
        }
      }

      // Build start_date (use local time format, not UTC)
      let startDate: string | null = null
      if (editStartDate) {
        startDate = `${editStartDate}T00:00:00`
      }

      await updateTodo(selectedTask.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        status: editStatus,
        start_date: startDate,
        due_date: dueDate
      })

      if (editStatus === 'completed' && selectedTask.status !== 'completed') {
        triggerFeedback('complete', { soundEnabled })
      } else {
        triggerFeedback('create', { soundEnabled })
      }

      setSelectedTask(null)
    } catch (error) {
      console.error('Failed to save task:', error)
      alert('タスクの保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }

  // Get status color (same as TodoItem)
  const getStatusColor = (status: Todo['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-gray-200 text-gray-600'
      case 'deleted': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Duplicate task (same as TodoItem)
  const handleDuplicateTask = async (todo: Todo) => {
    try {
      initializeFeedback()
      await addTodo({
        title: `${todo.title} (コピー)`,
        description: todo.description,
        priority: todo.priority,
        status: 'pending',
        start_date: todo.start_date,
        due_date: todo.due_date,
        project_id: todo.project_id,
        assigned_to_user_id: null,
        assignment_status: 'unassigned',
        progress_percentage: 0,
        deleted_at: null,
        thumbnail_image_id: null,
        thumbnail_size: 'small'
      })
      triggerFeedback('create', { soundEnabled })
      setSelectedTask(null)
    } catch (error) {
      console.error('Error duplicating task:', error)
      alert('タスクの複製に失敗しました')
    }
  }

  // Week day headers
  const weekDays = ['日', '月', '火', '水', '木', '金', '土']

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Calendar Header - コンパクト化 */}
      <div className="flex-shrink-0 flex items-center justify-between px-2 py-1 border-b">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <h2 className="text-sm font-semibold text-gray-900">
          {format(currentMonth, 'yyyy年M月', { locale: ja })}
        </h2>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              const today = new Date()
              setCurrentMonth(today)
              setSelectedDate(today)
            }}
            className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
          >
            <Clock className="w-3 h-3" />
            今日
          </button>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calendar Grid - コンパクト化 */}
      <div className="flex-shrink-0 px-2 py-1">
        {/* Week day headers */}
        <div className="grid grid-cols-7 mb-1">
          {weekDays.map((day, index) => (
            <div
              key={day}
              className={`text-center text-meta font-medium py-1 ${
                index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-500'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days - 週単位で期間タスクの帯を重ねて描画（帯はドラッグで日付変更可能） */}
        <DndContext
          sensors={barDragSensors}
          collisionDetection={pointerWithin}
          onDragStart={handleBarDragStart}
          onDragEnd={handleBarDragEnd}
          onDragCancel={() => setActiveBarDrag(null)}
        >
        <div className="border-t border-l border-gray-200">
          {weekBars.map(({ segments, laneCount }, w) => {
            // 週の最低高さ（帯レーン数に応じて自動で伸びる）
            const weekMinHeight = fontSize === 'small' ? 64 : fontSize === 'large' ? 80 : 72
            return (
              <div
                key={w}
                className="grid grid-cols-7"
                style={{
                  minHeight: `${weekMinHeight}px`,
                  gridTemplateRows: laneCount > 0
                    ? `22px repeat(${laneCount}, 19px) minmax(18px, 1fr)` // 最終行は件数バッジが帯に重ならない高さを確保
                    : '22px 1fr'
                }}
              >
                {/* 日付セル（背景・クリックターゲット、全行にまたがる） */}
                {calendarDays.slice(w * 7, w * 7 + 7).map((day, i) => {
                  const taskCount = getTaskCount(day)
                  const dayMilestones = getMilestonesForDate(day)
                  const isCurrentMonth = isSameMonth(day, currentMonth)
                  const isSelected = isSameDay(day, selectedDate)
                  const isTodayDate = isToday(day)
                  const dayOfWeek = day.getDay()
                  const holiday = getHoliday(day, holidayCountry)
                  const isHolidayDay = !!holiday

                  return (
                    <DroppableDayCell
                      key={day.toISOString()}
                      dateKey={format(day, 'yyyy-MM-dd')}
                      onClick={() => handleDateClick(day)}
                      className={`
                        relative flex flex-col items-start justify-start p-1 transition-colors border-r border-b border-gray-200
                        ${!isCurrentMonth ? 'opacity-40' : ''}
                        ${isSelected ? 'bg-blue-100' : isHolidayDay ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}
                      `}
                      style={{ gridColumn: `${i + 1} / span 1`, gridRow: '1 / -1' }}
                      title={holiday?.name}
                    >
                      <span
                        className={`
                          text-meta font-medium flex items-center justify-center rounded-full
                          ${isTodayDate ? 'bg-pink-500 text-white w-6 h-6' : ''}
                          ${!isTodayDate && (dayOfWeek === 0 || isHolidayDay) ? 'text-red-500' : ''}
                          ${!isTodayDate && dayOfWeek === 6 && !isHolidayDay ? 'text-blue-500' : ''}
                          ${!isTodayDate && dayOfWeek !== 0 && dayOfWeek !== 6 && !isHolidayDay ? 'text-gray-700' : ''}
                        `}
                      >
                        {format(day, 'd')}
                      </span>
                      {/* Milestone icons - 左下に表示 */}
                      {dayMilestones.length > 0 && (
                        <div className="absolute left-0.5 bottom-0.5 flex gap-0.5">
                          {dayMilestones.slice(0, 3).map((milestone) => (
                            <span
                              key={milestone.id}
                              className="text-[10px] leading-none"
                              title={milestone.title}
                            >
                              {milestone.icon_value}
                            </span>
                          ))}
                          {dayMilestones.length > 3 && (
                            <span className="text-[8px] text-gray-500">+{dayMilestones.length - 3}</span>
                          )}
                        </div>
                      )}
                      {/* Task count badge - 右下に塗り丸バッジ */}
                      {taskCount > 0 && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => handleTaskCountClick(day, e)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleTaskCountClick(day, e as unknown as React.MouseEvent)
                            }
                          }}
                          className="absolute right-0.5 bottom-0.5 z-20 min-w-[16px] h-4 px-1 bg-indigo-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center cursor-pointer hover:bg-indigo-700 transition-colors shadow-sm"
                        >
                          {taskCount}
                        </span>
                      )}
                    </DroppableDayCell>
                  )
                })}
                {/* 期間タスクの帯 */}
                {segments.map((seg) => {
                  const barColor = getProject(seg.todo.project_id)?.color || '#6366f1'
                  const isCompleted = seg.todo.status === 'completed'
                  const thumbUrl = seg.isStart ? barThumbUrls.get(seg.todo.id) : undefined
                  const segStartKey = format(calendarDays[w * 7 + seg.colStart - 1], 'yyyy-MM-dd')
                  return (
                    <DraggableTaskBar
                      key={`${seg.todo.id}-${seg.colStart}`}
                      dragId={`${seg.todo.id}@@${segStartKey}`}
                      todoId={seg.todo.id}
                      segStartKey={segStartKey}
                      onClick={(e) => handleBarClick(seg.todo, e)}
                      className={`z-10 min-w-0 flex items-center px-1 text-left text-[9px] sm:text-[10px] font-medium leading-none hover:brightness-110 transition-[filter] ${
                        isCompleted ? 'opacity-50' : ''
                      }`}
                      style={{
                        gridColumn: `${seg.colStart} / span ${seg.colSpan}`,
                        gridRow: seg.lane + 2,
                        backgroundColor: barColor,
                        color: getBarTextColor(barColor),
                        margin: '1px 2px',
                        borderRadius: `${seg.isStart ? 4 : 0}px ${seg.isEnd ? 4 : 0}px ${seg.isEnd ? 4 : 0}px ${seg.isStart ? 4 : 0}px`
                      }}
                      title={`${seg.todo.title}${seg.todo.start_date ? `（${format(parseLocalDate(seg.todo.start_date), 'M/d')}〜${format(parseLocalDate(seg.todo.due_date!), 'M/d')}）` : ''}`}
                    >
                      {/* 写真サムネイル（帯の開始セグメント左端に表示） */}
                      {thumbUrl && (
                        <img
                          src={thumbUrl}
                          alt=""
                          className="w-4 h-4 rounded-[3px] object-cover flex-shrink-0 mr-1 border border-white/40"
                          loading="lazy"
                        />
                      )}
                      <span className={`truncate ${isCompleted ? 'line-through' : ''}`}>{seg.todo.title}</span>
                    </DraggableTaskBar>
                  )
                })}
              </div>
            )
          })}
        </div>
        {/* ドラッグ中の帯のプレビュー */}
        <DragOverlay dropAnimation={null}>
          {activeBarDrag && (
            <div
              className="flex items-center px-2 py-1 rounded text-[10px] font-medium shadow-lg cursor-grabbing"
              style={{ backgroundColor: activeBarDrag.color, color: activeBarDrag.textColor }}
            >
              <span className="truncate max-w-[160px]">{activeBarDrag.title}</span>
            </div>
          )}
        </DragOverlay>
        </DndContext>
      </div>

      {/* Selected Date Header - コンパクト化 */}
      <div className="flex-shrink-0 px-2 py-1.5 bg-gray-50 border-y flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">
          {format(selectedDate, 'yyyy年M月d日(E)', { locale: ja })}
        </h3>
        <div className="flex items-center gap-2">
          {/* Sort Order Button */}
          <div className="relative">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              className="appearance-none text-xs bg-white border border-gray-300 rounded px-2 py-1 pr-6 cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="due_date">期限日順</option>
              <option value="start_date">開始日順</option>
              <option value="display_order">タスク順</option>
            </select>
            <ArrowDownUp className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <button
            onClick={(e) => handleAddTaskClick(selectedDate, e)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-3 h-3" />
            追加
          </button>
        </div>
      </div>

      {/* Task List - TodoItem風のスタイル（縦スクロールのみ） */}
      <div
        ref={taskListRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-2 pb-16 lg:pb-2"
        style={{ touchAction: 'pan-y' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {tasksWithDueDate.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            期限が設定されたタスクがありません
          </div>
        ) : (
          <>
            {tasksWithDueDate.map(todo => {
              const project = getProject(todo.project_id)
              const projectColor = project?.color || '#6366f1'
              const dueDate = parseLocalDate(todo.due_date!)
              const time = toTimeOnly(todo.due_date)
              const isTaskOnSelectedDate = isSameDay(dueDate, selectedDate)

              // TodoItem風のスタイル計算
              const backgroundColor = todo.status === 'completed' ? '#f3f4f6' : '#ffffff'
              const overlayColor = todo.status === 'completed' ? 'transparent' : getColorWithOpacity(projectColor, 0.15)
              const borderColor = todo.status === 'completed' ? '#9ca3af' : projectColor

              return (
                <div
                  key={todo.id}
                  ref={(el) => {
                    if (el) {
                      taskRefs.current.set(todo.id, el)
                    } else {
                      taskRefs.current.delete(todo.id)
                    }
                  }}
                  onClick={() => handleTaskClick(todo)}
                  className={`
                    rounded-r-lg px-3 py-2 cursor-pointer transition-all border-l-4 border border-gray-300 mb-2
                    ${todo.status === 'completed' ? 'opacity-60' : ''}
                    ${isTaskOnSelectedDate ? 'ring-2 ring-blue-400' : 'hover:shadow-md'}
                  `}
                  style={{
                    background: `linear-gradient(${overlayColor}, ${overlayColor}), ${backgroundColor}`,
                    borderLeftColor: borderColor,
                  }}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      {/* Status badge and Title */}
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 text-meta rounded-full font-medium ${getStatusColor(todo.status)}`}>
                          {todo.status === 'pending' ? '未着手' : todo.status === 'in_progress' ? '進行中' : '完了'}
                        </span>
                        <span className={`
                          task-title font-medium truncate
                          ${todo.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}
                        `}>
                          {todo.title}
                        </span>
                      </div>
                      {/* Date info */}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {/* Date Range - シンプルな形式 (12/16～12/19) */}
                        <span className="text-meta text-gray-500">
                          {todo.start_date
                            ? `${format(parseLocalDate(todo.start_date), 'M/d')}～${format(dueDate, 'M/d')}`
                            : format(dueDate, 'M/d(E)', { locale: ja })
                          }
                          {time && ` ${time}`}
                        </span>
                        {/* Project Badge */}
                        {project && (
                          <span
                            className="text-meta px-1.5 py-0.5 rounded font-medium"
                            style={{
                              backgroundColor: `${project.color}20`,
                              color: project.color
                            }}
                          >
                            {project.name}
                          </span>
                        )}
                        {/* Team Project: Assignee Badge and Progress */}
                        {project?.is_team_project && (
                          <div className="flex items-center gap-1.5">
                            {todo.assigned_user && (
                              <div
                                className={`h-4 w-4 rounded-full bg-gradient-to-br ${getUserColorFromId(todo.assigned_user.id)} flex items-center justify-center flex-shrink-0`}
                                title={todo.assigned_user.nickname || todo.assigned_user.email || 'Unknown'}
                              >
                                {todo.assigned_user.avatar_url ? (
                                  <img
                                    src={todo.assigned_user.avatar_url}
                                    alt={todo.assigned_user.nickname || 'User'}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="text-white text-[8px] font-semibold">
                                    {getUserInitial(todo.assigned_user.nickname, todo.assigned_user.email)}
                                  </span>
                                )}
                              </div>
                            )}
                            <span className="text-meta text-blue-600 font-medium">
                              {todo.progress_percentage || 0}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            {/* 最後のタスクがリスト上部までスクロールできるよう余白を追加 */}
            {tasksWithDueDate.length > 0 && (
              <div
                className="flex-shrink-0"
                style={{
                  height: 'calc(100% - 60px)',
                  minHeight: '100px',
                }}
                aria-hidden="true"
              />
            )}
          </>
        )}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9999]"
          onClick={() => setSelectedTask(null)}
        >
          <div
            className="bg-white w-full sm:w-[480px] sm:rounded-lg rounded-t-2xl max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - indigo color like AddTaskModal */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-indigo-600">
              <h3 className="text-lg font-semibold text-white">タスク詳細</h3>
              <button
                onClick={() => setSelectedTask(null)}
                className="p-1 hover:bg-indigo-500 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Content - Edit Form */}
            <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Status and Project */}
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as Todo['status'])}
                  className={`px-2 py-0.5 text-xs rounded-full font-medium cursor-pointer ${getStatusColor(editStatus)}`}
                >
                  <option value="pending">未着手</option>
                  <option value="in_progress">進行中</option>
                  <option value="completed">完了</option>
                </select>

                {/* Project Badge (read-only) */}
                {getProject(selectedTask.project_id) && (
                  <span
                    className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-white border border-gray-200"
                    style={{ color: getProject(selectedTask.project_id)?.color }}
                  >
                    {getProject(selectedTask.project_id)?.name}
                  </span>
                )}
              </div>

              {/* Task Title - Editable */}
              <div>
                <label className="block task-description font-medium text-gray-700 mb-1">
                  タスク名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg task-title focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="タスク名を入力"
                />
              </div>

              {/* Description - Editable */}
              <div>
                <label className="block task-description font-medium text-gray-700 mb-1">
                  メモ
                </label>
                {/* 他画面（TaskDetailModal等）と同じリッチテキストエディタを使用
                    （メモはHTML形式で保存されるため、素のtextareaだとタグが生表示されてしまう） */}
                <RichTextEditor
                  content={editDescription}
                  onChange={setEditDescription}
                  placeholder="メモを入力（任意）"
                  className="task-description"
                />
              </div>

              {/* Date Range - Editable */}
              <div>
                <label className="block task-description font-medium text-gray-700 mb-1">
                  期間
                </label>
                <div className="flex items-center gap-2">
                  {/* Start Date */}
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">開始日</label>
                    <div className="flex items-center gap-1">
                      <div
                        className="relative cursor-pointer flex-1"
                        onClick={() => editStartDateRef.current?.showPicker?.()}
                      >
                        <input
                          ref={editStartDateRef}
                          type="date"
                          value={editStartDate}
                          onChange={(e) => setEditStartDate(e.target.value)}
                          max={editDueDate || undefined}
                          className="w-full h-10 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                          style={{ colorScheme: 'light' }}
                        />
                        <div className="h-10 px-2 text-sm border rounded-lg bg-gray-50 flex items-center">
                          {editStartDate || 'YYYY-MM-DD'}
                        </div>
                      </div>
                      {editStartDate && (
                        <button
                          type="button"
                          onClick={() => setEditStartDate('')}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  <span className="text-gray-400 mt-5">〜</span>

                  {/* Due Date */}
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">期限</label>
                    <div className="flex items-center gap-1">
                      <div
                        className="relative cursor-pointer flex-1"
                        onClick={() => editDueDateRef.current?.showPicker?.()}
                      >
                        <input
                          ref={editDueDateRef}
                          type="date"
                          value={editDueDate}
                          onChange={(e) => setEditDueDate(e.target.value)}
                          min={editStartDate || undefined}
                          className="w-full h-10 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                          style={{ colorScheme: 'light' }}
                        />
                        <div className="h-10 px-2 text-sm border rounded-lg bg-gray-50 flex items-center">
                          {editDueDate || 'YYYY-MM-DD'}
                        </div>
                      </div>
                      {editDueDate && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditDueDate('')
                            setEditDueTime('')
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Time (shown when due date is selected) */}
                {editDueDate && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-500 mb-1">期限時刻（任意）</label>
                    <div
                      className="relative cursor-pointer w-32"
                      onClick={() => editDueTimeRef.current?.showPicker?.()}
                    >
                      <input
                        ref={editDueTimeRef}
                        type="time"
                        value={editDueTime}
                        onChange={(e) => setEditDueTime(e.target.value)}
                        className="w-full h-10 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                        style={{ colorScheme: 'light' }}
                      />
                      <div className="h-10 px-2 text-sm border rounded-lg bg-gray-50 flex items-center">
                        {editDueTime || 'HH:MM'}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Team Project: Assignee and Progress */}
              {getProject(selectedTask.project_id)?.is_team_project && getProject(selectedTask.project_id)?.team_id && (
                <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800">チームタスク設定</h4>

                  {/* Assignee */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-700">担当者</span>
                    </div>
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      {selectedTask.assigned_user ? (
                        <>
                          <div
                            className={`h-5 w-5 rounded-full bg-gradient-to-br ${getUserColorFromId(selectedTask.assigned_user.id)} flex items-center justify-center`}
                          >
                            {selectedTask.assigned_user.avatar_url ? (
                              <img
                                src={selectedTask.assigned_user.avatar_url}
                                alt={selectedTask.assigned_user.nickname || 'User'}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-[8px] font-semibold">
                                {getUserInitial(selectedTask.assigned_user.nickname, selectedTask.assigned_user.email)}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-700">{selectedTask.assigned_user.nickname || '担当者'}</span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">未割り当て</span>
                      )}
                    </button>
                  </div>

                  {/* Progress */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-700">進捗</span>
                    </div>
                    <button
                      onClick={() => setShowProgressPanel(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <span className="text-sm font-medium text-blue-600">{selectedTask.progress_percentage || 0}%</span>
                      <span className="text-xs text-gray-500">更新</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Attached Images */}
              {loadingAttachments ? (
                <div className="text-sm text-gray-500">添付ファイルを読み込み中...</div>
              ) : (
                <>
                  {/* Images Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Image className="w-4 h-4" />
                        <span>画像 ({taskImages.length})</span>
                      </div>
                      <button
                        onClick={() => setShowImageModal(true)}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        管理
                      </button>
                    </div>
                    {taskImages.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {taskImages.map((img, index) => (
                          <button
                            key={img.id}
                            className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPreviewImageIndex(index)
                            }}
                          >
                            <img
                              src={img.thumbnailUrl || img.image_url}
                              alt={img.file_name}
                              className="w-16 h-16 object-cover rounded-lg border hover:opacity-80 transition-opacity"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Files Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <File className="w-4 h-4" />
                        <span>ファイル ({taskFiles.length})</span>
                      </div>
                      <button
                        onClick={() => setShowFileModal(true)}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        管理
                      </button>
                    </div>
                    {taskFiles.length > 0 && (
                      <div className="space-y-2">
                        {taskFiles.map(file => (
                          <div
                            key={file.id}
                            className="flex items-start gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              {getCategoryIcon(file.file_category)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{file.file_name}</p>
                              <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-0.5">
                                <span className="capitalize">{file.file_category}</span>
                                <span>•</span>
                                <span>{formatFileSize(file.file_size)}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {canPreview(file) && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation()
                                    const url = await getFileUrl(file.file_url)
                                    window.open(url, '_blank')
                                  }}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="プレビュー"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  downloadFile(file.file_url, file.file_name)
                                }}
                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                title="ダウンロード"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons - Simplified: Duplicate, Delete, Save */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDuplicateTask(selectedTask)}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded"
                  aria-label="タスクを複製"
                >
                  <img src={ButtonL} alt="タスクを複製" className="h-6 w-6 sm:h-7 sm:w-7" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-1.5 sm:p-2 hover:bg-red-50 rounded"
                  aria-label="ゴミ箱に移動"
                >
                  <img src={ButtonF} alt="ゴミ箱に移動" className="h-6 w-6 sm:h-7 sm:w-7" />
                </button>
              </div>
              <button
                onClick={handleSaveTask}
                disabled={isSaving || !editTitle.trim()}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => {
          setShowAddTaskModal(false)
          setAddTaskDate(null)
        }}
        defaultDate={addTaskDate}
        defaultProjectId={projectId}
      />

      {/* Image Preview Modal */}
      {previewImageIndex !== null && taskImages.length > 0 && createPortal(
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-95"
          onClick={() => setPreviewImageIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setPreviewImageIndex(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full z-20"
            aria-label="閉じる"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Previous button */}
          {previewImageIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setPreviewImageIndex(previewImageIndex - 1)
              }}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-3 sm:p-2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full z-20"
              aria-label="前の画像"
            >
              <ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>
          )}

          {/* Next button */}
          {previewImageIndex < taskImages.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setPreviewImageIndex(previewImageIndex + 1)
              }}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-3 sm:p-2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full z-20"
              aria-label="次の画像"
            >
              <ChevronRight className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>
          )}

          {/* Image */}
          <div
            className="absolute flex items-center justify-center"
            style={{ top: '60px', bottom: '80px', left: '16px', right: '16px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {taskImages[previewImageIndex]?.optimizedUrl ? (
              <img
                src={taskImages[previewImageIndex].optimizedUrl}
                alt={taskImages[previewImageIndex].file_name}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="text-white">読み込み中...</div>
            )}
          </div>

          {/* Image info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent pt-8 pb-4 px-4">
            <div className="text-white text-center">
              <p className="text-sm truncate">{taskImages[previewImageIndex]?.file_name}</p>
              <p className="text-xs text-gray-400 mt-1">
                {previewImageIndex + 1} / {taskImages.length}
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image Modal */}
      {selectedTask && (
        <TodoImageModal
          todoId={selectedTask.id}
          todoTitle={selectedTask.title}
          isOpen={showImageModal}
          currentThumbnailId={selectedTask.thumbnail_image_id}
          currentThumbnailSize={selectedTask.thumbnail_size}
          onSetThumbnail={async (imageId, size) => {
            await updateTodo(selectedTask.id, { thumbnail_image_id: imageId, thumbnail_size: size })
          }}
          onClose={() => {
            setShowImageModal(false)
            // Reload attachments
            if (selectedTask) {
              getTodoImages(selectedTask.id).then(result => {
                if (result.success && result.images) {
                  Promise.all(
                    result.images.map(async (img) => ({
                      ...img,
                      thumbnailUrl: await getThumbnailUrl(img.image_url),
                      optimizedUrl: await getOptimizedImageUrl(img.image_url)
                    }))
                  ).then(setTaskImages)
                }
              })
            }
          }}
        />
      )}

      {/* File Modal */}
      {selectedTask && (
        <TodoFileModal
          todoId={selectedTask.id}
          todoTitle={selectedTask.title}
          isOpen={showFileModal}
          onClose={() => {
            setShowFileModal(false)
            // Reload files
            if (selectedTask) {
              getTodoFiles(selectedTask.id).then(result => {
                if (result.success && result.files) {
                  setTaskFiles(result.files)
                }
              })
            }
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="タスクの削除確認"
        message="このタスクをゴミ箱に移動しますか？"
        confirmText="OK"
        cancelText="キャンセル"
        variant="danger"
        onConfirm={() => {
          if (selectedTask) {
            handleStatusChange(selectedTask, 'deleted')
          }
          setShowDeleteConfirm(false)
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Assign Task Modal */}
      {selectedTask && getProject(selectedTask.project_id)?.is_team_project && (
        <AssignTaskModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          todoTitle={selectedTask.title}
          teamId={getProject(selectedTask.project_id)?.team_id || ''}
          currentAssignedUserId={selectedTask.assigned_to_user_id}
          onSelect={async (userId) => {
            // 選択後にDBに保存
            await updateTodo(selectedTask.id, {
              assigned_to_user_id: userId,
              assignment_status: userId ? 'assigned' : 'unassigned'
            })
            setShowAssignModal(false)
            fetchTodos()
          }}
        />
      )}

      {/* Task Progress Panel */}
      {selectedTask && getProject(selectedTask.project_id)?.is_team_project && showProgressPanel && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[10000]"
          onClick={() => setShowProgressPanel(false)}
        >
          <div
            className="bg-white w-full sm:w-[400px] sm:rounded-lg rounded-t-2xl max-h-[80vh] overflow-hidden mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <TaskProgressPanel
              currentProgress={selectedTask.progress_percentage || 0}
              onProgressSelect={async (progress: number) => {
                // 選択後にDBに保存
                await updateTodo(selectedTask.id, {
                  progress_percentage: progress
                })
                setShowProgressPanel(false)
                fetchTodos()
              }}
              onClose={() => setShowProgressPanel(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
